import React, { useContext, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import i18next from 'i18next'

import { FormInputsType } from '@pages/FormCreateOrEdit/types'

import { ACTION_CODE, EVENT_CODE_GANTT, GENERATOR_INPUT_TYPE, REGEX } from '@constants'
import { AutocompleteOption, SelectOption } from '@types'

import {
  GANTT_BIND_TYPE_LIST,
  GanttActionType,
  PageContext,
} from '@gantt/components/GanttCreateOrEdit'
import { EMPTY_COMMAND_FORM } from '@gantt/constants'
import {
  GanttActionOptionAutocompleteTarget,
  GanttActionOptionSelectEvent,
  getBindTypeOptionsByStrings,
  getOptionByStr,
  getSelectOptionDisabledEvent,
  getSelectOptionDisabledTarget,
  isSegmentTarget,
} from '@gantt/helpers'

type useInputsParams = {
  actionTypeOptions: SelectOption[]
  eventOptions: SelectOption[]
  watchEventCode: string
  targetOptions: AutocompleteOption[]
  methods: UseFormReturn<GanttActionType, any, undefined>
  watchActionCode?: ACTION_CODE
}

export const useInputs = ({
  actionTypeOptions,
  eventOptions,
  watchEventCode,
  targetOptions,
  methods,
  watchActionCode,
}: useInputsParams) => {
  const { modalType, currentActionTimelineList, currentRow, bindingValuesTimelineAction } =
    useContext(PageContext)
  const { setValue, resetField, watch } = methods
  const watchedId = watch('id')

  const handleChangeActionCode = (value: string | number) => {
    const getBind = () => {
      switch (value) {
        case ACTION_CODE.OPEN_CONTEXT_MENU:
          return GANTT_BIND_TYPE_LIST.CONTEXT_MENU
        case ACTION_CODE.EXECUTE_COMMAND:
          return GANTT_BIND_TYPE_LIST.COMMANDS
        case ACTION_CODE.OPEN_FORM_MODEL:
          return GANTT_BIND_TYPE_LIST.FORM
        case ACTION_CODE.OPEN_TOOLTIP:
          return GANTT_BIND_TYPE_LIST.TOOLTIP
        default:
          return null
      }
    }

    const dictAction = bindingValuesTimelineAction?.find(item => item.code === value)
    const title = dictAction?.title || dictAction?.code || value

    const bind = getBind()

    setValue('title', String(title))

    if (modalType === 'edit') {
      setValue('eventCode', null)
    } else {
      resetField('eventCode', { defaultValue: undefined })
    }

    setValue('actionField', { bindType: bind })

    if (value === ACTION_CODE.EXECUTE_COMMAND) {
      setValue('actionField.commands', EMPTY_COMMAND_FORM)
    }
  }

  const eventOptionsList: SelectOption[] = useMemo(() => {
    if (watchActionCode === ACTION_CODE.OPEN_CONTEXT_MENU) {
      return getBindTypeOptionsByStrings([
        EVENT_CODE_GANTT.ON_BAR_RIGHT_CLICK,
        EVENT_CODE_GANTT.ON_ROW_RIGHT_CLICK,
      ])
    }

    if (watchActionCode === ACTION_CODE.OPEN_TOOLTIP) {
      return getBindTypeOptionsByStrings([
        EVENT_CODE_GANTT.ON_TOOLTIP_BAR,
        EVENT_CODE_GANTT.ON_TOOLTIP_SEGMENT,
      ])
    }

    if (watchActionCode === ACTION_CODE.EXECUTE_COMMAND) {
      return [
        ...eventOptions,
        ...getBindTypeOptionsByStrings([
          EVENT_CODE_GANTT.ON_SEGMENT_RESIZE_IN_TIMELOG,
          EVENT_CODE_GANTT.ON_SEGMENT_RESIZE_IN_BACKLOG,
          EVENT_CODE_GANTT.ON_SEGMENT_MOVE_IN_TIMELOG,
          EVENT_CODE_GANTT.ON_SEGMENT_MOVE_IN_BACKLOG,
          EVENT_CODE_GANTT.ON_SEGMENT_MOVE_FROM_TIMELOG_TO_BACKLOG,
          EVENT_CODE_GANTT.ON_SEGMENT_MOVE_FROM_BACKLOG_TO_TIMELOG,
        ]),
      ]
    }

    return eventOptions
  }, [watchActionCode])

  const actionCodeOptions = useMemo(
    () => [
      ...actionTypeOptions,
      getOptionByStr(ACTION_CODE.OPEN_CONTEXT_MENU),
      getOptionByStr(ACTION_CODE.OPEN_TOOLTIP),
      getOptionByStr(ACTION_CODE.EXECUTE_COMMAND),
    ],
    [actionTypeOptions]
  )

  const actionInputs: FormInputsType[] = [
    {
      name: 'code',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: i18next.t('ganttCreate.actionForm.actionInputs.code.placeholder'),
      replacePattern: REGEX.MODEL_CODE_REPLACE_PATTERN,
      label: i18next.t('ganttCreate.actionForm.actionInputs.code.label'),
      disabled: modalType === 'edit',
      rules: { required: true, validate: value => value.trim().length !== 0 },
    },
    {
      name: 'actionCode',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      label: i18next.t('ganttCreate.actionForm.actionInputs.actionCode.label'),
      placeholder: i18next.t('placeholder.actionType'),
      selectOptions: actionCodeOptions,
      rules: { required: true },
      onChangeSelect: handleChangeActionCode,
      selectNoItemsMessage: i18next.t('ganttCreate.common.noItems'),
    },
    {
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      label: i18next.t('ganttCreate.actionForm.actionInputs.title.label'),
      name: 'title',
      placeholder: i18next.t('ganttCreate.actionForm.actionInputs.title.placeholder'),
    },
    {
      name: 'eventCode',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      label: i18next.t('ganttCreate.actionForm.actionInputs.eventCode.label'),
      placeholder: i18next.t('placeholder.event'),
      selectNoItemsMessage: i18next.t('ganttCreate.common.noItems'),
      selectOptions: eventOptionsList,
      renderValueSelect: value => <>{value}</>,
      rules: { required: true },
      getSelectOptionDisabled: option =>
        getSelectOptionDisabledEvent(option, currentActionTimelineList, watchedId),
      renderOptionSelect: option => {
        const selectOption = option as SelectOption

        return (
          <GanttActionOptionSelectEvent
            key={selectOption.id}
            list={currentActionTimelineList}
            option={selectOption}
          />
        )
      },
    },
    {
      name: 'targetRow',
      inputs: isSegmentTarget(watchEventCode)
        ? [
            {
              name: 'target',
              placeholder: i18next.t('ganttCreate.actionForm.actionInputs.target.placeholder'),
              inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
              multiple: true,
              label: i18next.t('ganttCreate.actionForm.actionInputs.target.label'),
              autocompleteOptions: targetOptions,
              rules: { required: true },
              getOptionDisabled: option =>
                getSelectOptionDisabledTarget(
                  option,
                  currentActionTimelineList,
                  watchedId,
                  watchActionCode,
                  watchEventCode
                ),
              renderAutocompleteOption: (
                props: React.HTMLAttributes<HTMLLIElement>,
                option: { id: string | number; label: string }
              ) => (
                <GanttActionOptionAutocompleteTarget
                  key={option.id}
                  autocompleteProps={props}
                  currentActionId={watchedId}
                  eventCode={watchEventCode as EVENT_CODE_GANTT}
                  list={currentActionTimelineList}
                  option={option}
                />
              ),
            },
          ]
        : [],
    },
  ]

  return actionInputs
}
